import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { loginWithEmail, signupWithEmail, loginWithGoogle, resetPassword } from '@/config/firebase';
import { toast } from 'sonner';
import { Loader2, Mail, Gift } from 'lucide-react';
import { validateEmail } from '@/utils/emailValidation';

const AuthModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        
        // Validate email
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
          toast.error(emailValidation.error);
          setLoading(false);
          return;
        }
        
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await signupWithEmail(email, password, name);
        toast.success('Account created successfully! Please verify your email to receive 100 free credits.');
        // Dispatch event to show verification modal
        window.dispatchEvent(new CustomEvent('signupSuccess'));
      } else if (mode === 'signin') {
        await loginWithEmail(email, password);
        toast.success('Signed in successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      toast.success('Signed in with Google!');
      onSuccess();
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error('Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success('Password reset email sent!');
      setMode('signin');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {mode === 'reset' ? 'Reset Password' : 'Welcome to Fluxara'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === 'reset'
              ? 'Enter your email to receive a password reset link'
              : 'Create amazing AI-powered T-shirt designs'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'reset' ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="reset-email-input"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleResetPassword}
              disabled={loading}
              data-testid="reset-password-button"
            >
              {loading ? <Loader2 className="animate-spin" /> : 'Send Reset Link'}
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setMode('signin')}>
              Back to Sign In
            </Button>
          </div>
        ) : (
          <>
            {/* Google Auth at the top */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleAuth}
              disabled={loading}
              data-testid="google-signin-button"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            <Tabs defaultValue="signin" className="w-full" onValueChange={setMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin" data-testid="signin-tab">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-4">
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="signin-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="signin-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="signin-submit-button">
                    {loading ? <Loader2 className="animate-spin" /> : 'Sign In'}
                  </Button>
                </form>
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => setMode('reset')}
                  data-testid="forgot-password-button"
                >
                  Forgot password?
                </Button>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4">
                {/* 100 Credits Banner */}
                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-cyan-900">Get 100 FREE Credits!</p>
                      <p className="text-xs text-cyan-700">Verify your email after signup to unlock your free credits</p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  <div>
                    <Label htmlFor="signup-name">Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="signup-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="signup-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="signup-confirm-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-button">
                    {loading ? <Loader2 className="animate-spin" /> : 'Create Account & Get 100 Credits'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;